import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowLeftRight, Check, Mail, Plus } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// The founding pilot program, from "Founding Pilot Program for Restaurant Owners" (Oct 2026).
// The application itself is its own page, /heart-of-house/pilot/apply (HohPilotForm).
const APPLY_PATH = "/heart-of-house/pilot/apply";

const HohPilotProgram = () => {
  const { t } = useLanguage();
  const pg = t.hoh.pilotPage;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = pg.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [pg.metaTitle]);

  return (
    <div data-testid="page-hoh-pilot">
      {/* HERO: a sunrise glow from below; headline left, a 2x2 grid of the places and the three groups right */}
      <section className="relative overflow-hidden border-b border-[#242424]">
        <div
          className="pointer-events-none absolute left-1/2 -bottom-[440px] h-[620px] w-[1100px] -translate-x-1/2 rounded-[100%] bg-[#E8490F]/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#E8490F]/70 to-transparent"
          aria-hidden
        />
        <div className="mise-container relative pt-10 md:pt-14 pb-14 md:pb-16">
          <Link
            to="/heart-of-house"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="hoh-pilot-back"
          >
            <ArrowLeft size={14} />
            {t.hoh.pilotForm.returnCta}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6 min-w-0">
              <p className="mise-eyebrow">{pg.eyebrow}</p>
              <h1 className="mt-4 text-4xl sm:text-5xl xl:text-[3.5rem] font-semibold tracking-tight leading-[1.04]">
                {pg.headingA} <span className="text-[#E8490F]">{pg.headingB}</span>
              </h1>
              <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">{pg.intro}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to={APPLY_PATH} className="mise-pill-primary" data-testid="hoh-pilot-apply-cta" data-track="hoh-pilot-apply-hero">
                  {pg.applyCta}
                  <ArrowRight size={16} />
                </Link>
                <a href={`mailto:${pg.seanEmail}`} className="mise-pill-secondary" data-track="hoh-pilot-email-sean">
                  <Mail size={16} />
                  {pg.talkCta}
                </a>
              </div>
            </div>

            <div className="lg:col-span-6 min-w-0">
              <div className="grid grid-cols-2 gap-3 max-w-[540px] mx-auto lg:ml-auto lg:mr-0" data-testid="hoh-pilot-hero-grid">
                <PlacesTile short={pg.placesShort} sub={pg.placesSub} label={pg.placesLabel} />
                {pg.groups.map((g, i) => (
                  <GroupTile key={g.name} n={i + 1} group={g} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUNDING OFFER (one ticket, three stubs), then the commitment and what you get */}
      <section className="py-14 md:py-20">
        <div className="mise-container">
          <p className="mise-eyebrow">{pg.offerEyebrow}</p>
          <div
            className="mt-5 grid md:grid-cols-3 rounded-2xl border border-[#444444] bg-gradient-to-b from-[#232323] to-[#1a1a1a] overflow-hidden"
            data-testid="hoh-pilot-offer"
          >
            {pg.offer.map((o, i) => (
              <div
                key={o.when}
                className={`relative px-6 py-5 md:px-7 md:py-6 ${
                  i > 0 ? "border-t md:border-t-0 md:border-l border-dashed border-[#4a4a4a]" : ""
                }`}
              >
                {i > 0 && (
                  <>
                    {/* the ticket's punched notches */}
                    <span className="hidden md:block absolute -top-2.5 -left-2.5 h-5 w-5 rounded-full bg-[#111111] border border-[#444444]" aria-hidden />
                    <span className="hidden md:block absolute -bottom-2.5 -left-2.5 h-5 w-5 rounded-full bg-[#111111] border border-[#444444]" aria-hidden />
                  </>
                )}
                <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#E8490F]">{o.when}</p>
                <p className="mt-2 text-3xl md:text-[2.1rem] font-semibold tracking-tight text-white leading-none">
                  {o.value}
                  {o.unit && <span className="ml-1.5 text-sm font-medium text-white/45">{o.unit}</span>}
                </p>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{o.text}</p>
              </div>
            ))}
          </div>

          <div className="relative mt-5 grid gap-5 lg:grid-cols-2" data-testid="hoh-pilot-give-get">
            <GiveGet heading={pg.giveHeading} items={pg.give} />
            <span className="hidden lg:flex absolute left-1/2 top-1/2 z-10 h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#E8490F]/60 bg-[#111111] text-[#E8490F] shadow-[0_0_0_6px_#111111]">
              <ArrowLeftRight size={18} />
            </span>
            <GiveGet heading={pg.getHeading} items={pg.get} accent />
          </div>
        </div>
      </section>

      {/* TIMELINE: a centre spine with the stops alternating sides */}
      <section className="py-14 md:py-20 border-t border-[#242424]">
        <div className="mise-container">
          <div className="text-center">
            <p className="mise-eyebrow">{pg.timelineEyebrow}</p>
            <h2 className="mt-3 text-2xl md:text-4xl font-semibold tracking-tight text-white">{pg.timelineHeading}</h2>
          </div>
          <ol className="relative mt-10 max-w-4xl mx-auto" data-testid="hoh-pilot-timeline">
            <span
              className="absolute top-2 bottom-2 left-[9px] md:left-1/2 w-px md:-translate-x-1/2 bg-gradient-to-b from-[#E8490F] via-[#E8490F]/50 to-[#E8490F]/10"
              aria-hidden
            />
            {pg.timeline.map((step, i) => {
              const right = i % 2 === 1;
              const last = i === pg.timeline.length - 1;
              return (
                <li key={step.when} className="relative pl-10 md:pl-0 pb-7 last:pb-0 md:grid md:grid-cols-2 md:gap-14">
                  <span
                    className={`absolute left-0 md:left-1/2 top-0.5 md:-translate-x-1/2 flex h-5 w-5 items-center justify-center rounded-full ${
                      last ? "bg-[#E8490F]" : "bg-[#111111] border-2 border-[#E8490F]"
                    }`}
                    aria-hidden
                  >
                    {last && <Check size={11} className="text-white" />}
                  </span>
                  <div className={`${right ? "md:col-start-2" : "md:text-right"} min-w-0`}>
                    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#E8490F]">{step.when}</p>
                    <h3 className="mt-1.5 text-base md:text-lg font-semibold text-white">{step.title}</h3>
                    <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{step.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* QUESTIONS OWNERS ASK */}
      <section className="py-14 md:py-20 border-t border-[#242424]">
        <div className="mise-container">
          <p className="mise-eyebrow">{pg.faqEyebrow}</p>
          <div className="mt-5 grid gap-x-10 lg:grid-cols-2" data-testid="hoh-pilot-faq">
            {pg.faq.map((item) => (
              <details key={item.q} className="group border-b border-[#2e2e2e]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-base font-medium text-white [&::-webkit-details-marker]:hidden">
                  {item.q}
                  <Plus size={16} className="flex-shrink-0 text-[#E8490F] transition-transform duration-200 group-open:rotate-45" aria-hidden />
                </summary>
                <p className="pb-5 -mt-1 text-sm text-white/65 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* APPLY BAND */}
      <section className="pb-14 md:pb-20">
        <div className="mise-container">
          <div className="relative overflow-hidden rounded-2xl border border-[#E8490F]/35 bg-gradient-to-r from-[#2b1810] via-[#1c1c1c] to-[#1a1a1a] px-7 py-8 md:px-10 md:py-9 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div
              className="pointer-events-none absolute -left-20 -top-20 h-60 w-60 rounded-full bg-[#E8490F]/20 blur-3xl"
              aria-hidden
            />
            <div className="relative max-w-xl">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">{pg.applyBandHeading}</h2>
              <p className="mt-2 text-white/65">{pg.applyBandText}</p>
            </div>
            <div className="relative flex flex-wrap gap-3">
              <Link to={APPLY_PATH} className="mise-pill-primary" data-testid="hoh-pilot-apply-band" data-track="hoh-pilot-apply-band">
                {pg.applyCta}
                <ArrowRight size={16} />
              </Link>
              <a href={`mailto:${pg.seanEmail}`} className="mise-pill-secondary" data-track="hoh-pilot-email-sean-band">
                <Mail size={16} />
                {pg.talkCta}
              </a>
            </div>
          </div>
          <p className="mt-10 text-xs text-white/35 leading-relaxed max-w-4xl">{pg.smallPrint}</p>
        </div>
      </section>
    </div>
  );
};

// The fifteen places: five orbs for each founding group.
const PlacesTile = ({ short, sub, label }) => (
  <div
    className="relative aspect-square overflow-hidden rounded-2xl border border-[#E8490F]/40 bg-gradient-to-br from-[#2b1810] to-[#161616] p-4 sm:p-5 flex flex-col"
    aria-label={label}
    data-testid="hoh-pilot-places"
  >
    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#E8490F]/25 blur-2xl" aria-hidden />
    <div className="relative grid grid-cols-5 gap-1.5 sm:gap-2.5">
      {Array.from({ length: 15 }, (_, i) => (
        <span
          key={i}
          className="flex aspect-square items-center justify-center rounded-full border border-[#E8490F]/50 bg-[#E8490F]/[0.08]"
          aria-hidden
        >
          <span
            className="h-1/3 w-1/3 rounded-full bg-[#E8490F] shadow-[0_0_10px_2px_rgba(232,73,15,0.6)] animate-pulse"
            style={{ animationDelay: `${i * 110}ms`, animationDuration: "2.4s" }}
          />
        </span>
      ))}
    </div>
    <div className="relative mt-auto">
      <p className="text-2xl sm:text-4xl font-semibold tracking-tight text-white leading-none whitespace-nowrap">{short}</p>
      <p className="mt-1.5 text-[11px] sm:text-xs text-white/55">{sub}</p>
    </div>
  </div>
);

const GroupTile = ({ n, group }) => (
  <div
    className="group relative aspect-square overflow-hidden rounded-2xl border border-[#333333] bg-[#181818] p-4 sm:p-5 flex flex-col transition-colors hover:border-[#E8490F]/50"
    data-testid={`hoh-pilot-group-${n - 1}`}
  >
    <span
      className="block text-4xl sm:text-5xl font-bold leading-none tracking-tighter text-transparent [-webkit-text-stroke:1.2px_rgba(232,73,15,0.6)] transition-colors group-hover:[-webkit-text-stroke:1.2px_#E8490F]"
      aria-hidden
    >
      0{n}
    </span>
    <div className="mt-auto">
      <h3 className="text-sm sm:text-lg font-semibold text-white leading-tight">{group.name}</h3>
      <p className="mt-1 hidden sm:block text-xs text-white/55 leading-snug">{group.text}</p>
      <p className="mt-2.5 inline-flex rounded-full bg-[#E8490F]/12 px-2.5 py-1 text-[10px] sm:text-[11px] font-semibold text-[#E8490F] leading-tight">
        {group.live}
      </p>
    </div>
  </div>
);

const GiveGet = ({ heading, items, accent }) => (
  <div
    className={`rounded-2xl border p-6 md:p-7 ${
      accent ? "border-[#E8490F]/40 bg-gradient-to-br from-[#2b1810] to-[#1a1a1a]" : "border-[#333333] bg-[#181818]"
    }`}
  >
    <h3 className={`text-xs font-semibold tracking-[0.22em] uppercase ${accent ? "text-[#E8490F]" : "text-white/60"}`}>
      {heading}
    </h3>
    <ul className="mt-4 divide-y divide-[#2e2e2e]">
      {items.map((item) => (
        <li key={item.title} className="py-3 first:pt-0 last:pb-0">
          <p className="font-semibold text-white">{item.title}</p>
          <p className="mt-0.5 text-sm text-white/60 leading-relaxed">{item.text}</p>
        </li>
      ))}
    </ul>
  </div>
);

export default HohPilotProgram;
